/**
 * Environment-aware public Supabase configuration (browser-safe values only).
 *
 * Priority, highest first:
 *  1. `window.__APP_PUBLIC_CONFIG__` — injected into the SSR HTML by the server
 *     from the AWS production configuration. This is how AWS production reaches
 *     the browser without baking anything into the build.
 *  2. `process.env.SUPABASE_URL` / `SUPABASE_PUBLISHABLE_KEY` — server side. On
 *     EC2 these are hydrated at boot from AWS Secrets Manager; in Lovable they
 *     come from the Lovable-managed `.env` (Lovable Cloud).
 *  3. Build-time `VITE_SUPABASE_URL` / `VITE_SUPABASE_PUBLISHABLE_KEY` — the
 *     Lovable Cloud values used by the Lovable preview bundle.
 *
 * Only the URL and the publishable key ever appear here. Service-role keys,
 * Google client secrets and APP_USER_CONNECTION_KEY_SECRET are server-only and
 * must never reach this module.
 */

export type PublicSupabaseSource = "aws-production" | "lovable-cloud";

export type PublicSupabaseConfig = {
  url: string;
  publishableKey: string;
  source: PublicSupabaseSource;
};

export const PUBLIC_CONFIG_GLOBAL = "__APP_PUBLIC_CONFIG__";

declare global {
  interface Window {
    __APP_PUBLIC_CONFIG__?: PublicSupabaseConfig;
  }
}

function viteEnv(name: string): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.[name] || undefined;
}

function serverEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  return process.env[name] || undefined;
}

/** Resolves the Supabase configuration for the current environment, or null. */
export function readPublicSupabaseConfig(): PublicSupabaseConfig | null {
  if (typeof window !== "undefined") {
    const injected = window[PUBLIC_CONFIG_GLOBAL];
    if (injected?.url && injected.publishableKey) return injected;
  }

  const envUrl = serverEnv("SUPABASE_URL");
  const envKey = serverEnv("SUPABASE_PUBLISHABLE_KEY");
  if (envUrl && envKey) {
    const fromAws = serverEnv("APP_SECRETS_SOURCE") === "aws-secrets-manager";
    return {
      url: envUrl,
      publishableKey: envKey,
      source: fromAws ? "aws-production" : "lovable-cloud",
    };
  }

  const viteUrl = viteEnv("VITE_SUPABASE_URL");
  const viteKey = viteEnv("VITE_SUPABASE_PUBLISHABLE_KEY");
  if (viteUrl && viteKey) {
    return { url: viteUrl, publishableKey: viteKey, source: "lovable-cloud" };
  }

  return null;
}

/** Same as above, but throws an environment-neutral error when unconfigured. */
export function requirePublicSupabaseConfig(): PublicSupabaseConfig {
  const config = readPublicSupabaseConfig();
  if (!config) throw new Error("Supabase configuration is missing.");
  return config;
}
